import { ComplementsRuleInputs, MultiParentTransformationsRuleInputs, TransformationRuleInputs } from "@rebuild/shared";

export enum ValidationError {
  // complements and recommendations
  IsLikelyAndOverrideExistingLineItem = 'Cannot have both "Override Existing Line Item" and "Is Likely" set to true',
  ChildLineItemNotSelected = "Child line item must be selected",
  InvalidChildLineItem = "Child line item no longer exists in the database",
  InvalidChildLineItemPermutation = "Child line item has an invalid permutation",
  ParentLineItemNotSelected = "Parent line item must be selected",
  InvalidParentLineItem = "Parent line item no longer exists in the database",
  InvalidParentLineItemPermutation = "Parent line item has an invalid permutation",

  // multi parent transformations
  MissingLineItemInExistingParam = "Existing Line Item Params is missing a Line Item",
  MissingLineItemInNewParam = "New Line Item Params is missing a Line Item",
  InvalidExistingLineItem = "Existing line item no longer exists in the database",
  InvalidNewLineItem = "New line item no longer exists in the database",
  InvalidQuantityFunction = "A valid quantity function must be selected",
  MissingQuantityValueForInherit = "Quantity value is required when using INHERIT_FROM_SPECIFIC_EXISTING",
  InvalidQuantityValueForInherit = "Quantity value must reference a the index of an existing line item param (1-indexed)",
  MissingQuantityValueForDefault = "Quantity value is required when using USE_DEFAULT",

  // line item transformation
  InitialLineItemNotSelected = "Initial line item must be selected",
  InvalidInitialLineItem = "Initial line item no longer exists in the database",
  InvalidFinalLineItem = "Final line item no longer exists in the database",
  InvalidFinalLineItemPermutation = "Final line item has an invalid permutation",
  MissingFinalLineItemPermutation = "A permutation must be selected when the final line item differs from the initial line item",
  MissingQuantityConversionType = "A quantity conversion type must be selected when a quantity conversion value is set",
  MissingQuantityConversionValue = "A quantity conversion value must be set when a quantity conversion type is selected",
  InvalidQuantityConversionType = "A valid quantity conversion type must be selected",
  OverrideMissingMatchingCriteria = "Override must have at least one of: run criteria, permutation, or quantity range defined",
  InvalidOverridePermutationLineItem = "Override permutation must reference the same line item as the initial line item",
}

export type ValidationErrorEntry = { validationError: ValidationError; isInOverride?: boolean };

/**
 * This is a Claude-generated helper type that accepts a TypeScript type and generates a list of strings where each
 * string is a path to a field in that TypeScript type. This helps us when it comes to displaying our validation
 * errors in the rules pop-up. We want to set our validation errors to be at the input field that triggers it, and
 * this is a lot easier of we can define the paths to these input fields ahead of type with `Paths<T>`.
 *
 * For example, if we have:
 * ```
 * 		type FakeType = {
 * 			a: string,
 * 			b: {
 * 				c: number
 * 			}
 * 		}
 *
 * 		type FakeTypePaths = Paths<FakeType>
 * ```
 * Then:
 * ```
 * FakeTypePaths = `a` | `b` | `b.c`
 * ```
 *
 * This doesn't do a great job at handling arrays since we don't know the length of the array ahead of time and
 * therefore can't generate a type ahead of time with all the possible indices in that array. Instead, we use the
 * string `[number]` to denote any index in an array and then replace that with the actual index (as a string) with
 * the helper function `resolveArrayPath`.
 */
export type Paths<T, Prefix extends string = ""> = {
  [K in keyof T & string]: NonNullable<T[K]> extends readonly (infer Item)[]
    ? Item extends object
      ? Paths<Item, `${Prefix}${K}[number].`> | `${Prefix}${K}`
      : `${Prefix}${K}`
    : NonNullable<T[K]> extends object
      ? `${Prefix}${K}` | Paths<NonNullable<T[K]>, `${Prefix}${K}.`>
      : `${Prefix}${K}`;
}[keyof T & string];

export const resolveArrayPath = <T>(path: Paths<T>, index: number) => (path as string).replace("[number]", `[${index}]`) as Paths<T>;

export type ComplementsRuleInputPaths = Paths<ComplementsRuleInputs>;

export type MultiParentTransformationsRuleInputPaths = Paths<MultiParentTransformationsRuleInputs>;

export type LineItemTransformationRuleInputPaths = Paths<TransformationRuleInputs>;
